import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionInputHandle, ActionValidator, RichMediaState } from '@bonusly/common-action-input';
import { useSharedConfig } from '@bonusly/common-give-box';
import { useAllowanceBoostId } from '@bonusly/common-rewards';
import { useEventListener } from '@bonusly/events';
import { useCurrentUser, useIsBizyEnabled } from '@bonusly/who-am-i';

import { Bonus } from '../../../../../../types/bonus';
import { useContextualizedBonusDetails } from '../../../hooks/use-contextualized-bonus-details';
import { useSayThanks } from '../../../hooks/use-say-thanks';
import { parseErrorMessage } from '../../../utils/error';
import { isAmountWithoutText } from '../../../utils/string';
import { useBudget } from './use-budget';
import { useCommentModules } from './use-comment-modules';
import { useCreateComment } from './use-create-comment';

interface UseCommentInputOptions {
	onError?: (error: unknown) => void;
	onSuccess?: VoidFunction;
	via?: string;
}

export const useCommentInput = (bonus: Bonus, options: UseCommentInputOptions = {}) => {
	const { t } = useTranslation('recognition');
	const inputRef = useRef<ActionInputHandle>(null);
	const user = useCurrentUser();
	const isBizyEnabled = useIsBizyEnabled();
	const { id: allowanceBoostId } = useAllowanceBoostId();
	const handlers = useSharedConfig({ includeGroups: true });
	const modules = useCommentModules(bonus);
	const budget = useBudget();
	const isOwnRecognition = useSayThanks(bonus);
	const { canUserAddOn, isUserReceiver } = useContextualizedBonusDetails(bonus);

	const selectedSuggestionRef = useRef<string | null>(null);

	const { isSubmitting, onSubmit } = useCreateComment(bonus, {
		onError: (err: unknown) => {
			const message = parseErrorMessage(err);
			inputRef.current?.error(message);

			options.onError?.(err);
		},
		onSuccess: () => {
			selectedSuggestionRef.current = null;
			options.onSuccess?.();
			inputRef.current?.reset();
		},
		via: options.via,
		selectedSuggestionRef,
	});

	const validator = useMemo(() => {
		return new ActionValidator({
			requireAmount: false,
			requireRecipient: false,
			requireBalance: user.giving_balance,
			requirePositiveAmount: false,
		});
	}, [user]);

	const onSuggestionSelected = (text: string) => {
		selectedSuggestionRef.current = text;
		inputRef.current?.insertWithHighlight(text);
		inputRef.current?.focus();
	};

	const onSuggestionCleared = useCallback(() => {
		selectedSuggestionRef.current = null;
	}, []);

	const calculateReceiversFn = useCallback(
		(includeGiver: boolean) => {
			const recipients = bonus.recipients?.map((recipient) => (recipient.type === 'group' ? recipient.userIds : [recipient.id])) ?? [];
			const uniqueRecipients = new Set(recipients.flat());
			return uniqueRecipients.size + Number(includeGiver) - Number(isUserReceiver);
		},
		[bonus, isUserReceiver]
	);

	const calculateTotalFn = useCallback(
		(data: RichMediaState, state: { includeGiver: boolean }) => {
			const recipients = calculateReceiversFn(state.includeGiver);
			return Number(data.amount) * recipients;
		},
		[calculateReceiversFn]
	);

	const updateInput = useCallback(
		({ bonus: target, text }: { bonus: Bonus; text: string }) => {
			if (target.id === bonus.id) {
				const currentText = inputRef.current?.data().text ?? '';

				if (!text && isAmountWithoutText(currentText)) {
					inputRef.current?.reset();
				} else {
					inputRef.current?.insert(text);
				}

				inputRef.current?.focus();
			}
		},
		[bonus.id]
	);

	useEventListener('init-comment', updateInput);

	return {
		allowanceBoostId,
		budget,
		canUserAddOn,
		context: 'comment',
		calculateTotalFn,
		calculateReceiversFn,
		isOwnRecognition,
		isSubmitting,
		modules,
		placeholder: isOwnRecognition
			? t(isBizyEnabled ? 'comment.input_thanks_placeholder_with_bizy_hint' : 'comment.input_thanks_placeholder')
			: t(isBizyEnabled ? 'comment.input_placeholder_with_bizy_hint' : 'comment.input_placeholder'),
		onSubmit,
		ref: inputRef,
		onSuggestionSelected,
		onSuggestionCleared,
		validator,
		...handlers,
	};
};
